import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, ChevronDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import Seo, { breadcrumbLd, orgRef, ORG_ID } from "../components/Seo";
import { routePath, SITE_URL, blogArticlePath } from "../lib/routes";
import { micro } from "../lib/microcopy";

export default function BlogArticle({ lang = "en" }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const blogPath = routePath("blog", lang);
  const artPath = (l, s) => blogArticlePath(l, s);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/public/blog/${slug}`, { params: { language: lang } })
      .then(({ data }) => setArticle(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, lang]);

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-white"><Loader2 size={22} className="animate-spin text-[#9a9d98]" /></div>;
  if (notFound || !article) return (
    <div className="min-h-screen bg-white">
      <Seo lang={lang} title={`${t("blog.notFound")} | Equestrian Partners`} description="" path={artPath(lang, slug)} alternates={[]} noindex />
      <Nav solid />
      <div className="h-[60px] md:h-[68px]" />
      <main id="main" className="flex min-h-[60vh] flex-col items-center justify-center gap-4" data-testid="blog-article-notfound">
        <p className="font-display text-2xl font-light text-[#050505]">{t("blog.notFound")}</p>
        <button onClick={() => navigate(blogPath)} className="text-sm font-medium text-[#0e7c6b]">{t("blog.allArticles")}</button>
      </main>
      <Footer />
    </div>
  );

  const backend = process.env.REACT_APP_BACKEND_URL;
  const img = article.hero_url ? `${backend}${article.hero_url}` : "/og/og-blog.jpg";
  const alternates = [
    { lang: article.language || lang, path: artPath(article.language || lang, article.slug) },
    ...(article.translations || []).map((tr) => ({ lang: tr.language, path: artPath(tr.language, tr.slug) })),
  ];
  const ld = [
    { "@context": "https://schema.org", "@type": "BlogPosting",
      headline: article.title, description: article.meta_description || article.excerpt || "",
      image: img.startsWith("http") ? img : `${SITE_URL}${img}`,
      datePublished: article.published_at || undefined, dateModified: article.updated_at || article.published_at || undefined,
      inLanguage: article.language || lang,
      mainEntityOfPage: `${SITE_URL}${artPath(lang, article.slug)}`,
      author: { "@type": "Organization", name: "Equestrian Partners", "@id": ORG_ID },
      publisher: orgRef() },
    breadcrumbLd([
      { name: micro(lang, "home"), path: routePath("home", lang) },
      { name: "Blog", path: blogPath },
      { name: article.title, path: artPath(lang, article.slug) },
    ]),
  ];

  return (
    <div data-testid="blog-article-public" className="min-h-screen bg-white">
      <Seo lang={lang} title={article.meta_title || `${article.title} | Equestrian Partners`}
        description={article.meta_description || article.excerpt || ""} image={img} type="article"
        path={artPath(lang, article.slug)} alternates={alternates} jsonLd={ld} />
      <Nav solid />
      <div className="h-[60px] md:h-[68px]" />
      <main id="main">
      <article className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        <button onClick={() => navigate(blogPath)} data-testid="blog-article-back"
          className="mb-10 inline-flex items-center gap-2 text-sm font-light text-[#4a4a4a] transition-colors hover:text-[#050505]">
          <ArrowLeft size={16} /> {t("blog.allArticles")}
        </button>

        {article.published_at && <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#0e7c6b]">{article.published_at.slice(0, 10)}</p>}
        {article.translations?.length > 0 && (
          <div className="mt-3 flex items-center gap-2" data-testid="blog-article-langs">
            <span className="text-[11px] font-light uppercase tracking-wide text-[#9a9d98]">{article.language}</span>
            {article.translations.map((tr) => (
              <Link key={tr.slug} data-testid={`blog-lang-${tr.language}`} to={artPath(tr.language, tr.slug)}
                className="rounded-full border border-black/12 px-2.5 py-0.5 text-[11px] font-medium uppercase text-[#0e7c6b] hover:bg-[#0e7c6b]/10">
                {tr.language}
              </Link>
            ))}
          </div>
        )}
        <h1 className="mt-3 font-display text-4xl font-light leading-tight tracking-tight text-[#050505] md:text-5xl" data-testid="blog-article-title">{article.title}</h1>
        {article.excerpt && <p className="mt-5 text-lg font-light leading-relaxed text-[#6b6f6c]">{article.excerpt}</p>}

        {article.hero_url && (
          <div className="mt-10 aspect-[16/9] overflow-hidden rounded-3xl bg-[#f0efec]">
            <img src={`${backend}${article.hero_url}`} alt={article.title} width="1536" height="1024" className="h-full w-full object-cover" data-testid="blog-article-hero" />
          </div>
        )}

        <div className="prose prose-neutral mt-12 max-w-none font-light leading-relaxed text-[#2a2b29]
          prose-headings:font-display prose-headings:font-normal prose-headings:text-[#050505]
          prose-h2:text-2xl prose-h2:mt-10 prose-h3:text-xl prose-a:text-[#0e7c6b] prose-strong:text-[#050505]
          prose-li:marker:text-[#0e7c6b]" data-testid="blog-article-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content || ""}</ReactMarkdown>
        </div>

        {article.faq?.length > 0 && (
          <div className="mt-16" data-testid="blog-article-faq">
            <h2 className="font-display text-2xl font-normal text-[#050505]">{t("blog.faqTitle")}</h2>
            <div className="mt-6 divide-y divide-black/8 border-y border-black/8">
              {article.faq.map((f, i) => (
                <div key={i}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`blog-faq-q-${i}`} aria-expanded={openFaq === i}
                    className="flex w-full items-center justify-between gap-4 py-5 text-left">
                    <span className="text-base font-normal text-[#050505]">{f.q}</span>
                    <ChevronDown size={18} className={`shrink-0 text-[#9a9d98] transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && <p className="pb-5 text-base font-light leading-relaxed text-[#6b6f6c]">{f.a}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {(article.cta_label || article.cta_text) && (
          <div className="mt-16 rounded-3xl bg-[#141514] p-8 text-center text-white md:p-12" data-testid="blog-article-cta">
            {article.cta_text && <p className="mx-auto max-w-lg text-lg font-light leading-relaxed">{article.cta_text}</p>}
            {article.cta_label && (
              <a href={article.cta_url || routePath("contact", lang)} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0e7c6b] px-7 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90">
                {article.cta_label}
              </a>
            )}
          </div>
        )}
      </article>
      </main>
      <Footer />
    </div>
  );
}
